/**
 * El relato del plan — qué se le cuenta al usuario en cada estado.
 *
 * Existe porque Dirección encontró, corriendo el piloto, que la pantalla decía
 * dos cosas distintas a la vez: el párrafo narraba el plan de 2 horas que había
 * RECHAZADO, mientras el compromiso de abajo mostraba los 44 minutos de la
 * contrapropuesta que sí aceptó. El dato decía la verdad y la prosa mentía.
 *
 * La causa: el veredicto se arma UNA vez, al enviar el brief, y quedaba pegado
 * en pantalla aunque después el plan cambiara. Peor: el bloque que sí describía
 * lo comprometido estaba atado a los botones de decisión, así que desaparecía
 * justo cuando el texto viejo pasaba a ser engañoso.
 *
 * 🔑 Regla: el relato SIEMPRE se deriva del plan vigente. El veredicto original
 * sólo sobrevive mientras sigue siendo cierto —plan propuesto, sin tocar—, y
 * cuando no hay plan, que es el único caso donde no hay nada que derivar.
 *
 * Vive acá y no dentro de la vista para que se pueda probar sin renderizar.
 */

export type FasePlan = 'pendiente' | 'aprobado' | 'rechazado';

export interface PlanVigente {
  readonly entregable: string;
  readonly nivel: string;
  readonly minutos: number;
  readonly reducido: boolean;
  readonly pasos: number;
}

export function relatoDelPlan(
  fase: FasePlan,
  plan: PlanVigente | null,
  veredictoOriginal: string,
): string {
  // Sin plan no hay nada que derivar: el veredicto explica por qué no lo hubo.
  if (!plan) return veredictoOriginal;

  if (fase === 'aprobado') {
    return `Aprobaste: ${plan.entregable} — ${plan.nivel}. ` +
      `${plan.pasos} pasos, ${plan.minutos} minutos desde tu aprobación. ` +
      'El reloj ya corre.';
  }

  if (fase === 'rechazado') {
    return 'No hubo acuerdo sobre el alcance, así que no hay nada comprometido. ' +
      'Cuando quieras lo rearmamos con otro alcance.';
  }

  // Pendiente y ya contrapropuesto: el veredicto original describe el plan
  // completo, que en este punto es justamente el que se descartó.
  if (plan.reducido) {
    return `Rechazaste el plazo del plan completo. Esto es lo que sí entra: ` +
      `${plan.entregable} — ${plan.nivel}. ${plan.pasos} pasos, ${plan.minutos} minutos ` +
      'desde que aprobás. El reloj arranca cuando aprobás, no antes.';
  }

  // Pendiente y sin tocar: el veredicto original sigue siendo cierto.
  return veredictoOriginal;
}
